import { Fragment, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { DevOverlay } from '../../components/DevOverlay/DevOverlay';
import { API_URL } from '../../config/api';
import { readApiJson } from '../../utils/apiErrors';

const EMPTY_ARRAY = [];
const EMPTY_QUOTA_CONFIG = { reps: [], manual_deals: [], settings: { aop_target: 0, nam_aop_target: 0, apac_aop_target: 0 } };

function fM(value) {
  return `$${(Math.abs(Number(value || 0)) / 1000000).toFixed(2)}M`;
}

function fK(value) {
  return `$${(Math.abs(Number(value || 0)) / 1000).toFixed(0)}K`;
}

function fFull(value) {
  return `$${Math.abs(Math.round(Number(value || 0))).toLocaleString('en-US')}`;
}

function getCustomer(row) {
  return row.customer || row.end_user || row.bill_to || row.contract_name || '-';
}

function getProduct(row) {
  return row.sub_product || row.product_l2 || row.product || row.product_type || '-';
}

function changeBucket(row, month) {
  return row?.monthly_changes?.[month] || row?.changes?.[month] || {};
}

function changeAmount(bucket, key) {
  const aliases = key === 'New'
    ? ['New', 'new']
    : ['Upsell', 'upsell', 'UpSell', 'up_sell'];
  return aliases.reduce((sum, alias) => sum + Number(bucket?.[alias] || 0), 0);
}

function colorForPct(pct) {
  if (pct >= 100) return '#15803d';
  if (pct >= 75) return '#16a34a';
  if (pct >= 50) return '#d97706';
  if (pct >= 25) return '#f97316';
  return '#dc2626';
}

function emojiForPct(pct) {
  if (pct >= 100) return '🏆';
  if (pct >= 75) return '🟢';
  if (pct >= 50) return '🟡';
  if (pct >= 25) return '🟠';
  return '🔴';
}

function statusForPct(pct) {
  if (pct >= 100) return 'On Track';
  if (pct >= 75) return 'Good';
  if (pct >= 50) return 'Needs Attention';
  return 'At Risk';
}

function Barometer({ pct, size = 160 }) {
  const h = Math.round(size * 0.57);
  const cx = size / 2;
  const cy = h - 4;
  const rx = size * 0.44;
  const capped = Math.min(Math.max(Number(pct || 0), 0), 100);
  const needlePct = Math.min(Math.max(Number(pct || 0), 0), 150);
  const endAngle = -Math.PI + (capped / 100) * Math.PI;
  const ex = cx + rx * Math.cos(endAngle);
  const ey = cy + rx * Math.sin(endAngle);
  const largeArc = capped > 50 ? 1 : 0;
  const needleAngle = (needlePct / 150) * 180 - 90;
  const nx = cx + rx * 0.82 * Math.cos((needleAngle - 90) * Math.PI / 180);
  const ny = cy + rx * 0.82 * Math.sin((needleAngle - 90) * Math.PI / 180);
  const color = colorForPct(pct);

  return (
    <svg width={size} height={h} viewBox={`0 0 ${size} ${h}`}>
      <path d={`M ${cx - rx} ${cy} A ${rx} ${rx} 0 0 1 ${cx + rx} ${cy}`} fill="none" stroke="#e2e8f0" strokeWidth="10" strokeLinecap="round" />
      <path d={`M ${cx - rx} ${cy} A ${rx} ${rx} 0 ${largeArc} 1 ${ex.toFixed(1)} ${ey.toFixed(1)}`} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" />
      <line x1={cx} y1={cy} x2={nx.toFixed(1)} y2={ny.toFixed(1)} stroke="#1a202c" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r="4" fill="#1a202c" />
      <text x={cx - rx + 2} y={cy + 12} fontSize="8" fill="#94a3b8">0%</text>
      <text x={cx} y="10" fontSize="8" fill="#94a3b8" textAnchor="middle">50%</text>
      <text x={cx + rx - 2} y={cy + 12} fontSize="8" fill="#94a3b8" textAnchor="end">100%</text>
    </svg>
  );
}

function MiniBarChart({ months, deals, color }) {
  const monthTotals = months.map(month => ({
    month,
    value: deals.filter(deal => deal.month === month).reduce((sum, deal) => sum + deal.amount, 0),
  }));
  const max = Math.max(...monthTotals.map(row => row.value), 1);

  return (
    <div className="arr-t7-mini-chart">
      {monthTotals.map(row => (
        <div className="arr-t7-mini-col" key={row.month}>
          <div className="arr-t7-mini-bar-wrap">
            <div
              className="arr-t7-mini-bar"
              style={{ height: `${Math.max(row.value ? 4 : 0, (row.value / max) * 100)}%`, background: color }}
              title={`${row.month}: ${fK(row.value)}`}
            />
          </div>
          <span>{row.month.slice(5)}</span>
        </div>
      ))}
    </div>
  );
}

function QuotaModal({ detail, onClose }) {
  if (!detail) return null;
  if (typeof document === 'undefined') return null;
  const color = detail.color || colorForPct(detail.pct);
  // Rendered via a portal straight to <body> — without it, this modal was
  // nesting inside whatever stacking context/transformed ancestor wraps this
  // tab, which let the navbar (its own stacking context) render on top of a
  // "fixed" overlay that was supposed to cover the whole viewport.
  return createPortal(
    <div className="arr-modal-ov" onClick={onClose}>
      <div className="arr-t7-html-modal" onClick={event => event.stopPropagation()}>
        <div className="arr-t7-html-head">
          <div>
            <div className="arr-t7-html-title">{detail.title}</div>
            <div className="arr-t7-html-sub">{detail.subtitle}</div>
          </div>
          <button className="arr-t7-html-close" onClick={onClose}>x</button>
        </div>
        <div className="arr-t7-html-kpis">
          {detail.kpis.map((kpi, index) => (
            <div className="arr-t7-html-kpi" key={kpi.label} style={{ borderRight: index < detail.kpis.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
              <div>{kpi.label}</div>
              <strong style={{ color: kpi.color }}>{kpi.value}</strong>
            </div>
          ))}
        </div>
        <div className="arr-t7-html-body">
          <div className="arr-t7-html-baro">
            <div className="arr-t7-html-baro-label">Attainment</div>
            <Barometer pct={detail.pct} size={180} />
            <strong style={{ color }}>{detail.pct.toFixed(1)}%</strong>
            <span>{emojiForPct(detail.pct)} {statusForPct(detail.pct)}</span>
          </div>
          <div className="arr-t7-html-chart">
            <div className="arr-t7-html-section-title">Monthly Bookings</div>
            <MiniBarChart months={detail.months} deals={detail.deals} color={color} />
          </div>
        </div>
        <div className="arr-t7-modal-table-wrap">
          {detail.extra}
          <div className="arr-t7-html-section-title">
            Deal-Level Breakdown (YTD Bookings)
            <span className="arr-t7-deal-count">{detail.deals.length} deal{detail.deals.length === 1 ? '' : 's'} · {fFull(detail.deals.reduce((sum, deal) => sum + deal.amount, 0))} total</span>
          </div>
          <div className="arr-twrap-fixed">
          <table className="arr-t7-html-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Product</th>
                <th>Type</th>
                <th>Month</th>
                <th className="right">Amount</th>
                <th>Booking Type</th>
                <th>Rep</th>
              </tr>
            </thead>
            <tbody>
              {detail.deals.map((deal, index) => (
                <tr key={`${deal.rep}-${deal.customer}-${deal.month}-${index}`}>
                  <td><strong>{deal.customer}</strong></td>
                  <td>{deal.product}</td>
                  <td><span className={`arr-t7-pill ${deal.type === 'New' ? 'new' : 'upsell'}`}>{deal.type}</span></td>
                  <td>{deal.month}</td>
                  <td className="right money" style={{ color: deal.type === 'New' ? '#1e5fa8' : '#15803d' }}>{fFull(deal.amount)}</td>
                  <td><span className="arr-t7-muted-pill">{deal.btype || 'Commercial'}</span></td>
                  <td>{deal.rep}</td>
                </tr>
              ))}
              {!detail.deals.length && <tr><td colSpan={7} className="bm-empty-row">No FY26 bookings found.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Editable replacement for what used to be the hardcoded QUOTA_2026 /
// AOP_TARGET_2026 / per-region AOP targets / DAN_DABROWSKI_CODEALS at the
// top of this file — reps, AOP targets, and manual deal credits are now all
// edited here and saved to ArrQuotaRep/ArrQuotaSettings/ArrQuotaManualDeal
// (arr_views/quota.py) instead of requiring a code change.
// Adds `current` to `options` when it's non-empty and not already there —
// keeps a <select> from silently blanking out an existing value that
// predates (or no longer matches) today's option list.
function withCurrent(options, current) {
  return current && !options.includes(current) ? [...options, current] : options;
}

function QuotaEditorModal({ config, customerOptions = EMPTY_ARRAY, productOptions = EMPTY_ARRAY, onClose, onSaved }) {
  const [reps, setReps] = useState(() => (config.reps || []).map(row => ({ ...row })));
  const [deals, setDeals] = useState(() => (config.manual_deals || []).map(row => ({ ...row })));
  const [settings, setSettings] = useState(() => ({ ...config.settings }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const repKeyOptions = useMemo(
    () => Array.from(new Set(reps.map(row => row.rep_key).filter(Boolean))),
    [reps],
  );

  if (typeof document === 'undefined') return null;

  const updateRep = (index, field, value) => setReps(rows => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  const addRep = () => setReps(rows => [...rows, { rep_key: '', display_name: '', quota: 0, region: 'NAM', type: 'AE' }]);
  const removeRep = (index) => setReps(rows => rows.filter((_, i) => i !== index));

  const updateDeal = (index, field, value) => setDeals(rows => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  const addDeal = () => setDeals(rows => [...rows, { rep_key: '', customer: '', product: '', amount: 0, month: '', deal_type: 'New', booking_type: '' }]);
  const removeDeal = (index) => setDeals(rows => rows.filter((_, i) => i !== index));

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/arr/quota/save/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reps: reps.filter(row => row.rep_key.trim()).map(row => ({ ...row, quota: Number(row.quota) || 0 })),
          manual_deals: deals.filter(row => row.rep_key.trim() && row.month.trim()).map(row => ({ ...row, amount: Number(row.amount) || 0 })),
          settings: {
            aop_target: Number(settings.aop_target) || 0,
            nam_aop_target: Number(settings.nam_aop_target) || 0,
            apac_aop_target: Number(settings.apac_aop_target) || 0,
          },
        }),
      });
      await readApiJson(res, 'Could not save Quota/AOP settings.');
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="arr-modal-ov" onClick={onClose}>
      <div className="arr-t7-html-modal" onClick={event => event.stopPropagation()} style={{ maxWidth: 980 }}>
        <div className="arr-t7-html-head">
          <div>
            <div className="arr-t7-html-title">Edit Quota & AOP Targets</div>
            <div className="arr-t7-html-sub">Changes apply across the whole Quota vs AOP tab once saved</div>
          </div>
          <button className="arr-t7-html-close" onClick={onClose}>x</button>
        </div>

        <div className="arr-qe-body">
          {error && <div className="arr-qe-error">{error}</div>}

          <div className="arr-qe-section"><span className="arr-qe-section-title">AOP Targets</span></div>
          <div className="arr-qe-grid">
            {[
              ['aop_target', 'Company-wide AOP Target'],
              ['nam_aop_target', 'NAM AOP Target'],
              ['apac_aop_target', 'APAC AOP Target'],
            ].map(([key, label]) => (
              <label className="arr-qe-field" key={key}>
                <span className="arr-qe-field-label">{label}</span>
                <input
                  className="arr-qe-input"
                  type="number"
                  value={settings[key]}
                  onChange={e => setSettings(s => ({ ...s, [key]: e.target.value }))}
                />
              </label>
            ))}
          </div>

          <div className="arr-qe-section">
            <span className="arr-qe-section-title">Reps & Quotas</span>
            <button className="arr-qe-btn-add" onClick={addRep}>+ Add Rep</button>
          </div>
          <div className="arr-qe-table-wrap">
            <table className="arr-qe-table">
              <thead>
                <tr>
                  <th>Rep Key (matches Sales Person)</th>
                  <th>Display Name</th>
                  <th className="right">Quota</th>
                  <th>Region</th>
                  <th>Type</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {reps.map((row, index) => (
                  <tr key={index}>
                    <td><input className="arr-qe-input" value={row.rep_key} onChange={e => updateRep(index, 'rep_key', e.target.value)} /></td>
                    <td><input className="arr-qe-input" value={row.display_name} onChange={e => updateRep(index, 'display_name', e.target.value)} /></td>
                    <td><input className="arr-qe-input" type="number" value={row.quota} onChange={e => updateRep(index, 'quota', e.target.value)} /></td>
                    <td>
                      <select className="arr-qe-select" value={row.region} onChange={e => updateRep(index, 'region', e.target.value)}>
                        <option value="NAM">NAM</option>
                        <option value="APAC">APAC</option>
                      </select>
                    </td>
                    <td>
                      <select className="arr-qe-select" value={row.type} onChange={e => updateRep(index, 'type', e.target.value)}>
                        <option value="AE">AE</option>
                        <option value="Partner">Partner</option>
                      </select>
                    </td>
                    <td><button className="arr-qe-btn-remove" title="Remove rep" onClick={() => removeRep(index)}>x</button></td>
                  </tr>
                ))}
                {!reps.length && <tr><td colSpan={6} className="arr-qe-table-empty">No reps yet — click "+ Add Rep".</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="arr-qe-section">
            <span className="arr-qe-section-title">Manual Deal Credits</span>
            <span className="arr-qe-section-hint">(bookings not already in the ARR data, e.g. a partner co-sell credit)</span>
            <button className="arr-qe-btn-add" onClick={addDeal}>+ Add Deal</button>
          </div>
          <div className="arr-qe-table-wrap">
            <table className="arr-qe-table">
              <thead>
                <tr>
                  <th>Rep Key</th>
                  <th>Customer</th>
                  <th>Product</th>
                  <th className="right">Amount</th>
                  <th>Month (YYYY-MM)</th>
                  <th>Type</th>
                  <th>Booking Type</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {deals.map((row, index) => (
                  <tr key={index}>
                    <td>
                      <select className="arr-qe-select" value={row.rep_key} onChange={e => updateDeal(index, 'rep_key', e.target.value)}>
                        <option value="">— Select Rep —</option>
                        {withCurrent(repKeyOptions, row.rep_key).map(key => <option key={key} value={key}>{key}</option>)}
                      </select>
                    </td>
                    <td>
                      <select className="arr-qe-select" value={row.customer} onChange={e => updateDeal(index, 'customer', e.target.value)}>
                        <option value="">— Select Customer —</option>
                        {withCurrent(customerOptions, row.customer).map(name => <option key={name} value={name}>{name}</option>)}
                      </select>
                    </td>
                    <td>
                      <select className="arr-qe-select" value={row.product} onChange={e => updateDeal(index, 'product', e.target.value)}>
                        <option value="">— Select Product —</option>
                        {withCurrent(productOptions, row.product).map(name => <option key={name} value={name}>{name}</option>)}
                      </select>
                    </td>
                    <td><input className="arr-qe-input" type="number" value={row.amount} onChange={e => updateDeal(index, 'amount', e.target.value)} /></td>
                    <td><input className="arr-qe-input" placeholder="2026-04" value={row.month} onChange={e => updateDeal(index, 'month', e.target.value)} /></td>
                    <td>
                      <select className="arr-qe-select" value={row.deal_type} onChange={e => updateDeal(index, 'deal_type', e.target.value)}>
                        <option value="New">New</option>
                        <option value="Upsell">Upsell</option>
                      </select>
                    </td>
                    <td><input className="arr-qe-input" value={row.booking_type} onChange={e => updateDeal(index, 'booking_type', e.target.value)} /></td>
                    <td><button className="arr-qe-btn-remove" title="Remove deal" onClick={() => removeDeal(index)}>x</button></td>
                  </tr>
                ))}
                {!deals.length && <tr><td colSpan={8} className="arr-qe-table-empty">No manual deal credits.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="arr-qe-footer">
          <button className="arr-qe-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="arr-qe-btn-save" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function QuotaTab({ data, canManageData }) {
  const records = data.records || EMPTY_ARRAY;
  const [filter, setFilter] = useState('ALL');
  const [modal, setModal] = useState(null);
  const [quotaConfig, setQuotaConfig] = useState(EMPTY_QUOTA_CONFIG);
  const [quotaLoading, setQuotaLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  // Known Customer/Product values straight from the ARR bookings data — lets
  // the Manual Deal Credits editor offer them as dropdown choices instead of
  // free text, same source each deal's own Customer/Product would otherwise
  // need to be typed by hand (and match) against.
  const customerOptions = useMemo(
    () => Array.from(new Set(records.map(getCustomer))).filter(Boolean).sort(),
    [records],
  );
  const productOptions = useMemo(
    () => Array.from(new Set(records.map(getProduct))).filter(Boolean).sort(),
    [records],
  );

  const loadQuotaConfig = () => {
    setQuotaLoading(true);
    return fetch(`${API_URL}/api/arr/quota/`, { credentials: 'include' })
      .then(res => readApiJson(res, 'Could not load Quota/AOP settings.'))
      .then(body => setQuotaConfig(body))
      .catch(() => {})
      .finally(() => setQuotaLoading(false));
  };

  useEffect(() => { loadQuotaConfig(); }, []);

  // Previously the hardcoded QUOTA_2026 object — now built from whatever was
  // last saved via the in-page editor below (see arr_views/quota.py).
  const quotaMap = useMemo(() => {
    const map = {};
    (quotaConfig.reps || []).forEach(row => {
      map[row.rep_key] = {
        quota: Number(row.quota) || 0,
        region: row.region || '',
        type: row.type || '',
        displayName: row.display_name || row.rep_key,
      };
    });
    return map;
  }, [quotaConfig.reps]);

  const aopTarget = Number(quotaConfig.settings?.aop_target) || 0;
  const namAopTarget = Number(quotaConfig.settings?.nam_aop_target) || 0;
  const apacAopTarget = Number(quotaConfig.settings?.apac_aop_target) || 0;
  const manualDeals = quotaConfig.manual_deals || EMPTY_ARRAY;

  const metrics = useMemo(() => {
    const discoveredMonths = new Set(data.all_months || []);
    records.forEach(row => {
      Object.keys(row.monthly_arr || {}).forEach(month => discoveredMonths.add(month));
      Object.keys(row.monthly_changes || row.changes || {}).forEach(month => discoveredMonths.add(month));
    });
    // The quota figures (QUOTA_2026/AOP_TARGET_2026) are annual targets, so
    // the window's START must stay fixed at the fiscal year's Jan 1 — only
    // the "as of" cutoff should move with the global filter bar's Period
    // selector, matching how the other tabs anchor to data.period.to.
    const latest = data.period?.to || data.latest_month || data.kpis?.latest_month || [...discoveredMonths].filter(Boolean).sort().at(-1) || '2026-06';
    const fy26Months = [...discoveredMonths]
      .filter(month => month >= '2026-01' && month <= latest)
      .sort();

    if (!fy26Months.length) {
      ['2026-01', '2026-02', '2026-03', '2026-04', '2026-05', '2026-06'].forEach(month => fy26Months.push(month));
    }

    const actuals = {};
    const repDeals = {};
    const allDeals = [];

    records.forEach(row => {
      const rep = row.sales_person || row.sales_rep || '';
      if (!rep) return;
      fy26Months.forEach(month => {
        const bucket = changeBucket(row, month);
        [
          { type: 'New', amount: changeAmount(bucket, 'New') },
          { type: 'Expansion', amount: changeAmount(bucket, 'Upsell') },
        ].forEach(item => {
          if (item.amount <= 0) return;
          actuals[rep] = (actuals[rep] || 0) + item.amount;
          const deal = {
            customer: getCustomer(row),
            product: getProduct(row),
            type: item.type,
            month,
            amount: item.amount,
            btype: row.booking_type || row.revenue_type || row.line_of_business || 'Commercial',
            rep,
          };
          (repDeals[rep] = repDeals[rep] || []).push(deal);
          allDeals.push(deal);
        });
      });
    });

    manualDeals.forEach(deal => {
      if (!fy26Months.includes(deal.month)) return;
      const rep = deal.rep_key;
      const amount = Number(deal.amount) || 0;
      actuals[rep] = (actuals[rep] || 0) + amount;
      const row = {
        customer: deal.customer, product: deal.product, amount,
        month: deal.month, type: deal.deal_type, btype: deal.booking_type, rep,
      };
      (repDeals[rep] = repDeals[rep] || []).push(row);
    });

    Object.values(repDeals).forEach(list => list.sort((a, b) => b.amount - a.amount));
    allDeals.sort((a, b) => b.amount - a.amount);

    const rows = Object.entries(quotaMap).map(([key, quota]) => {
      const actual = actuals[key] || 0;
      const pct = quota.quota > 0 ? (actual / quota.quota) * 100 : 0;
      return {
        dataKey: key,
        rep: quota.displayName,
        quota: quota.quota,
        region: quota.region,
        type: quota.type,
        actual,
        pct,
        gap: quota.quota - actual,
        deals: repDeals[key] || [],
      };
    }).sort((a, b) => b.pct - a.pct);

    const aeRows = rows.filter(row => row.type === 'AE');
    const aeQuota = aeRows.reduce((sum, row) => sum + row.quota, 0);
    const aeActual = aeRows.reduce((sum, row) => sum + row.actual, 0);
    const aePct = aeQuota ? (aeActual / aeQuota) * 100 : 0;
    const totalAllBookings = allDeals.reduce((sum, deal) => sum + deal.amount, 0);
    const aopPct = aopTarget ? (totalAllBookings / aopTarget) * 100 : 0;

    // Reps with real bookings but no entry in the quota roster (e.g. a new
    // hire not yet added, or a leadership/overlay role that doesn't carry a
    // quota) — computed from the data instead of hardcoded, so a new name
    // here always surfaces automatically rather than being silently dropped
    // from every quota view like Akshay Garg was until this was added.
    const unrosteredReps = Object.entries(actuals)
      .filter(([rep]) => !quotaMap[rep])
      .map(([rep, actual]) => ({ rep, actual }))
      .sort((a, b) => b.actual - a.actual);

    return {
      latest,
      months: fy26Months,
      rows,
      allDeals,
      totalAllBookings,
      aopPct,
      aopGap: aopTarget - totalAllBookings,
      aeQuota,
      aeActual,
      aePct,
      aeGap: aeQuota - aeActual,
      namQuota: aeRows.filter(row => row.region === 'NAM').reduce((sum, row) => sum + row.quota, 0),
      apacQuota: aeRows.filter(row => row.region === 'APAC').reduce((sum, row) => sum + row.quota, 0),
      namBookings: aeRows.filter(row => row.region === 'NAM').reduce((sum, row) => sum + row.actual, 0),
      apacBookings: aeRows.filter(row => row.region === 'APAC').reduce((sum, row) => sum + row.actual, 0),
      onTrack: aeRows.filter(row => row.pct >= 50).length,
      aeCount: aeRows.length,
      actuals,
      unrosteredReps,
    };
  }, [data, records, quotaMap, aopTarget, manualDeals]);

  const filteredRows = metrics.rows.filter(row => {
    if (filter === 'ALL') return true;
    if (filter === 'AE') return row.type === 'AE';
    if (filter === 'Partner') return row.type === 'Partner';
    return row.region === filter;
  });

  const openAllModal = () => {
    setModal({
      title: 'Total Bookings - All Reps',
      subtitle: `FY26 YTD: ${metrics.months[0]} -> ${metrics.months.at(-1)}`,
      pct: metrics.aopPct,
      color: colorForPct(metrics.aopPct),
      months: metrics.months,
      deals: metrics.allDeals,
      kpis: [
        { label: 'FY26 AOP Target', value: fM(aopTarget), color: '#1e5fa8' },
        { label: 'Total Bookings (All Reps)', value: fM(metrics.totalAllBookings), color: '#6d28d9' },
        { label: 'AOP Attainment', value: `${metrics.aopPct.toFixed(1)}%`, color: colorForPct(metrics.aopPct) },
        { label: 'Gap to AOP', value: fM(Math.abs(metrics.aopGap)), color: metrics.aopGap > 0 ? '#dc2626' : '#15803d' },
      ],
    });
  };

  const openRepModal = (row) => {
    setModal({
      title: `${row.rep} - Booking Detail`,
      subtitle: `FY26 YTD: ${metrics.months[0]} -> ${metrics.months.at(-1)} · ${row.type}`,
      pct: row.pct,
      color: colorForPct(row.pct),
      months: metrics.months,
      deals: row.deals,
      kpis: [
        { label: row.type === 'Partner' ? 'Reference Quota' : '2026 Quota', value: fM(row.quota), color: '#1e5fa8' },
        { label: 'YTD Bookings', value: fM(row.actual), color: '#15803d' },
        { label: 'Attainment', value: `${row.pct.toFixed(1)}%`, color: colorForPct(row.pct) },
        { label: row.type === 'Partner' ? 'Reference Gap' : 'Gap to Quota', value: fM(Math.abs(row.gap)), color: row.gap > 0 ? '#dc2626' : '#15803d' },
      ],
    });
  };

  const openQuotaModal = () => {
    const regionRows = (
      <div className="arr-t7-region-modal">
        <div className="arr-t7-html-section-title">Region & Rep Breakdown</div>
        <QuotaTable rows={metrics.rows} onOpen={openRepModal} namAopTarget={namAopTarget} apacAopTarget={apacAopTarget} compact />
      </div>
    );
    setModal({
      title: 'Sales Quota Reps - Region Breakdown',
      subtitle: `FY26 YTD: ${metrics.months[0]} -> ${metrics.months.at(-1)} · AE Reps Only (Partners Excluded)`,
      pct: metrics.aePct,
      color: colorForPct(metrics.aePct),
      months: metrics.months,
      deals: metrics.allDeals.filter(deal => quotaMap[deal.rep]?.type === 'AE'),
      extra: regionRows,
      kpis: [
        { label: 'Total AE Quota', value: fM(metrics.aeQuota), color: '#1e5fa8' },
        { label: 'FY26 YTD Bookings', value: fM(metrics.aeActual), color: '#15803d' },
        { label: 'Attainment', value: `${metrics.aePct.toFixed(1)}%`, color: colorForPct(metrics.aePct) },
        { label: 'Gap to Quota', value: fM(Math.abs(metrics.aeGap)), color: metrics.aeGap > 0 ? '#dc2626' : '#15803d' },
      ],
    });
  };

  const aopCards = [
    { label: 'FY26 AOP Target', value: fM(aopTarget), color: '#1e5fa8', sub: 'Official FY26 Annual Operating Plan' },
    { label: 'Total Bookings - All Reps', value: fM(metrics.totalAllBookings), color: '#6d28d9', sub: metrics.unrosteredReps.length ? `No quota assigned — ${metrics.unrosteredReps.map(({ rep, actual }) => `${rep}: ${fM(actual)}`).join(' · ')}` : 'All contributors are on the AE/Partner quota roster' },
    { label: 'AOP Attainment', value: `${metrics.aopPct.toFixed(1)}%`, color: colorForPct(metrics.aopPct), sub: 'vs $17.33M AOP Target' },
    { label: 'Gap to AOP', value: fM(Math.abs(metrics.aopGap)), color: metrics.aopGap > 0 ? '#dc2626' : '#15803d', sub: metrics.aopGap > 0 ? 'Behind AOP Target' : 'Ahead of AOP Target' },
  ];

  const quotaCards = [
    { label: 'Total Sales Quota', value: fM(metrics.aeQuota), color: '#1e5fa8', sub: `NAM: ${fM(metrics.namQuota)} · APAC: ${fM(metrics.apacQuota)}` },
    { label: 'FY26 YTD Bookings\n(Excluding Leaders)', value: fM(metrics.aeActual), color: colorForPct(metrics.aePct), sub: `NAM: ${fM(metrics.namBookings)} · APAC: ${fM(metrics.apacBookings)} · ${metrics.onTrack}/${metrics.aeCount} reps >=50%` },
    { label: 'Attainment vs Quota', value: `${metrics.aePct.toFixed(1)}%`, color: colorForPct(metrics.aePct), sub: `vs ${fM(metrics.aeQuota)} total quota · ${metrics.aeCount} AE reps` },
    { label: 'Gap to Quota', value: fM(Math.abs(metrics.aeGap)), color: metrics.aeGap > 0 ? '#dc2626' : '#15803d', sub: metrics.aeGap > 0 ? `Behind quota · ${metrics.onTrack}/${metrics.aeCount} reps >=50%` : 'Ahead of quota' },
  ];

  if (quotaLoading && !quotaConfig.reps.length) {
    return <div className="esd-state">Loading Quota vs AOP...</div>;
  }

  return (
    <>
      <QuotaModal detail={modal} onClose={() => setModal(null)} />
      {editing && (
        <QuotaEditorModal
          config={quotaConfig}
          customerOptions={customerOptions}
          productOptions={productOptions}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); loadQuotaConfig(); }}
        />
      )}

      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#1e40af', lineHeight: 1.5 }} className="arr-qe-note">
        <span>📌 <strong>Note:</strong> the dashboard's date filter only partially applies here. Quotas are annual FY26 targets, so this tab always tracks attainment starting January 1 — the filter's <strong>From</strong> date (including Custom range starts) doesn't move that. Only the <strong>To</strong> date shifts the "as of" cutoff used for YTD bookings below.</span>
        {canManageData && (
          <button className="arr-qe-trigger" onClick={() => setEditing(true)}>
            ✏️ Edit Quota / AOP
          </button>
        )}
      </div>

      <div className="arr-t7-kpi-grid">
        {aopCards.map(card => (
          <button className="arr-t7-kcard" key={card.label} onClick={openAllModal} title="Click for details">
            <div>{card.label}</div>
            <strong style={{ color: card.color }}>{card.value}</strong>
            <span>{card.sub}</span>
            <small>Click for details</small>
          </button>
        ))}
      </div>

      <div className="arr-t7-kpi-grid arr-t7-kpi-grid-second">
        {quotaCards.map(card => (
          <button className="arr-t7-kcard" key={card.label} onClick={openQuotaModal} title="Click for details">
            <div>{card.label}</div>
            <strong style={{ color: card.color }}>{card.value}</strong>
            <span>{card.sub}</span>
            <small>Click for details</small>
          </button>
        ))}
      </div>

      <div className="arr-t7-filters">
        <span>Filter:</span>
        {[
          ['ALL', 'All'],
          ['NAM', 'North America'],
          ['APAC', 'APAC'],
          ['AE', 'AEs'],
          ['Partner', 'Partners'],
        ].map(([key, label]) => (
          <button key={key} className={filter === key ? 'on' : ''} onClick={() => setFilter(key)}>{label}</button>
        ))}
      </div>

      <div className="arr-t7-baro-grid">
        {filteredRows.map(row => {
          const color = colorForPct(row.pct);
          return (
            <DevOverlay key={row.dataKey} name={`Quota Card: ${row.rep}`}>
              <button className="arr-t7-baro-card" onClick={() => openRepModal(row)} title="Click for deal breakdown">
                <div className="arr-t7-baro-head">
                  <div>
                    <strong>{row.rep}</strong>
                    <span>{row.region} · {row.type}</span>
                  </div>
                  <em className={row.pct >= 50 ? 'warm' : ''}>{emojiForPct(row.pct)} {statusForPct(row.pct)}</em>
                </div>
                <div className="arr-t7-baro-center">
                  <Barometer pct={row.pct} />
                  <b style={{ color }}>{row.pct.toFixed(1)}%</b>
                </div>
                <div className="arr-t7-baro-stats">
                  <span>Actual: <strong>{fK(row.actual)}</strong></span>
                  <span>Quota: <strong>{fK(row.quota)}</strong></span>
                </div>
                <div className="arr-t7-track"><i style={{ width: `${Math.min(row.pct, 100).toFixed(1)}%`, background: color }} /></div>
                <div className="arr-t7-baro-foot">
                  <span>Click for deals</span>
                  <span>Gap: <strong style={{ color: row.gap > 0 ? '#dc2626' : '#15803d' }}>{fK(row.gap)}</strong></span>
                </div>
              </button>
            </DevOverlay>
          );
        })}
      </div>

      <div className="arr-t7-table-card">
        <QuotaTable rows={filteredRows} allRows={metrics.rows} onOpen={openRepModal} namAopTarget={namAopTarget} apacAopTarget={apacAopTarget} showTotal />
      </div>
    </>
  );
}

function QuotaTable({ rows, allRows, onOpen, showTotal = false, compact = false, namAopTarget = 0, apacAopTarget = 0 }) {
  const sections = [
    { key: 'NAM_AE', label: '🌎 North America (NAM)', aopTarget: namAopTarget, color: '#1e5fa8', bg: '#e8f1fb', filter: row => row.region === 'NAM' && row.type === 'AE' },
    { key: 'APAC_AE', label: '🌏 Asia Pacific (APAC)', aopTarget: apacAopTarget, color: '#0891b2', bg: '#e0f7fa', filter: row => row.region === 'APAC' && row.type === 'AE' },
    { key: 'PARTNER', label: '🤝 Partners', color: '#7c3aed', bg: '#f3e8ff', filter: row => row.type === 'Partner' },
  ];
  const sourceRows = allRows || rows;
  const visibleKeys = new Set(rows.map(row => row.dataKey));
  let aeOnlyQuota = 0;
  let aeOnlyActual = 0;

  return (
    <div className="arr-twrap arr-t7-table-wrap">
      <table className="arr-t7-quota-table">
        <thead>
          <tr>
            <th>Rep / Region</th>
            <th>BU</th>
            <th>Type</th>
            <th className="right">2026 Quota</th>
            <th className="right">YTD Bookings</th>
            <th className="right">Attainment</th>
            <th className="right">Gap</th>
            <th>Progress</th>
          </tr>
        </thead>
        <tbody>
          {sections.map(section => {
            const sectionRows = sourceRows.filter(row => visibleKeys.has(row.dataKey)).filter(section.filter);
            if (!sectionRows.length) return null;
            const isPartner = section.key === 'PARTNER';
            const quota = sectionRows.reduce((sum, row) => sum + row.quota, 0);
            const actual = sectionRows.reduce((sum, row) => sum + row.actual, 0);
            const pct = quota ? (actual / quota) * 100 : 0;
            const gap = quota - actual;
            const color = isPartner ? '#7c3aed' : colorForPct(pct);
            if (!isPartner) {
              aeOnlyQuota += quota;
              aeOnlyActual += actual;
            }
            return (
              <Fragment key={section.key}>
                <tr className="arr-t7-section-row" style={{ background: section.bg, borderTopColor: section.color }} key={`${section.key}-head`}>
                  <td colSpan={3}>
                    {section.label}
                    {section.aopTarget && <span>AOP Target: ${(section.aopTarget / 1000000).toFixed(1)}M</span>}
                    {isPartner && <span>Ref credit only · excluded from company quota</span>}
                  </td>
                  <td className="right money" style={{ color: section.color }}>{isPartner ? 'N/A' : fFull(quota)}</td>
                  <td className="right money" style={{ color: '#15803d' }}>{fFull(actual)}</td>
                  <td className="right" style={{ color }}>{isPartner ? '-' : `${pct.toFixed(1)}%`}</td>
                  <td className="right money" style={{ color: isPartner ? '#9ca3af' : gap > 0 ? '#dc2626' : '#15803d' }}>{isPartner ? '-' : fFull(gap)}</td>
                  <td>{!isPartner && <div className="arr-t7-table-track"><i style={{ width: `${Math.min(pct, 100).toFixed(1)}%`, background: color }} /></div>}</td>
                </tr>
                {sectionRows.map(row => {
                  const rowColor = isPartner ? '#7c3aed' : colorForPct(row.pct);
                  return (
                    <tr className={`arr-t7-data-row ${isPartner ? 'partner' : ''}`} key={row.dataKey} onClick={() => onOpen(row)}>
                      <td><strong>{row.rep}</strong></td>
                      <td>{row.region}</td>
                      <td><span className={`arr-t7-type ${isPartner ? 'partner' : ''}`}>{row.type}</span></td>
                      <td className="right money" style={{ color: isPartner ? '#9ca3af' : '#1e5fa8' }}>{isPartner ? 'Ref only' : fFull(row.quota)}</td>
                      <td className="right money" style={{ color: '#15803d' }}>{fFull(row.actual)}</td>
                      <td className="right" style={{ color: rowColor }}>{isPartner ? '-' : `${row.pct.toFixed(1)}%`}</td>
                      <td className="right money" style={{ color: isPartner ? '#9ca3af' : row.gap > 0 ? '#dc2626' : '#15803d' }}>{isPartner ? '-' : fFull(row.gap)}</td>
                      <td>{!isPartner && <div className="arr-t7-table-track"><i style={{ width: `${Math.min(row.pct, 100).toFixed(1)}%`, background: rowColor }} /></div>}</td>
                    </tr>
                  );
                })}
                {!isPartner && (
                  <tr className="arr-t7-subtotal-row" style={{ borderBottomColor: section.color }} key={`${section.key}-subtotal`}>
                    <td colSpan={3}>↳ {section.label.replace(/^.+? /, '')} Subtotal (Rep Quotas)</td>
                    <td className="right money" style={{ color: section.color }}>{fFull(quota)}</td>
                    <td className="right money">{fFull(actual)}</td>
                    <td className="right" style={{ color }}>{pct.toFixed(1)}%</td>
                    <td className="right money" style={{ color: gap > 0 ? '#dc2626' : '#15803d' }}>{fFull(gap)}</td>
                    <td />
                  </tr>
                )}
                {isPartner && (
                  <tr className="arr-t7-partner-note" key={`${section.key}-note`}>
                    <td colSpan={3}>↳ Partner Total Bookings (informational)</td>
                    <td className="right money">-</td>
                    <td className="right money">{fFull(actual)}</td>
                    <td colSpan={3}>Not included in company quota or TOTAL row below</td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {!compact && (() => {
            const pct = aeOnlyQuota ? (aeOnlyActual / aeOnlyQuota) * 100 : 0;
            const gap = aeOnlyQuota - aeOnlyActual;
            const color = colorForPct(pct);
            return (
              <tr className="arr-t7-grand-row">
                <td colSpan={3}>📊 NAM + APAC Combined (AE Reps)</td>
                <td className="right money">{fFull(aeOnlyQuota)}</td>
                <td className="right money">{fFull(aeOnlyActual)}</td>
                <td className="right" style={{ color }}>{pct.toFixed(1)}%</td>
                <td className="right money" style={{ color: gap > 0 ? '#dc2626' : '#15803d' }}>{fFull(gap)}</td>
                <td><div className="arr-t7-table-track"><i style={{ width: `${Math.min(pct, 100).toFixed(1)}%`, background: color }} /></div></td>
              </tr>
            );
          })()}
        </tbody>
        {showTotal && (() => {
          const aeRows = (allRows || rows).filter(row => row.type === 'AE');
          const quota = aeRows.reduce((sum, row) => sum + row.quota, 0);
          const actual = aeRows.reduce((sum, row) => sum + row.actual, 0);
          const pct = quota ? (actual / quota) * 100 : 0;
          const gap = quota - actual;
          return (
            <tfoot>
              <tr>
                <td colSpan={3}>TOTAL (AE Reps) <span>Partner bookings excluded from quota total</span></td>
                <td className="right money">{fFull(quota)}</td>
                <td className="right money">{fFull(actual)}</td>
                <td className="right" style={{ color: colorForPct(pct) }}>{pct.toFixed(1)}%</td>
                <td className="right money" style={{ color: gap > 0 ? '#dc2626' : '#15803d' }}>{fFull(gap)}</td>
                <td />
              </tr>
            </tfoot>
          );
        })()}
      </table>
    </div>
  );
}

export default QuotaTab;
export { QuotaTab };
